import { create } from "zustand";
import { Document, Folder } from "~/api/model";
import { getSelectableDocuments } from "../helpers";

/**
 * Selection keeps the immutable identifiers of a document only. `uploadId` is
 * needed to recognise the same source file across versions, where copies get
 * their own `id`. Everything else stays owned by the documents query so the
 * selection can never hold stale document data.
 */
export interface SelectedDocument {
  id: string;
  uploadId?: string;
}

const toSelectedDocument = ({ id, uploadId }: Document): SelectedDocument => ({
  id,
  uploadId,
});

interface DocumentSelectionState {
  selectedDocuments: SelectedDocument[];
  toggleDocumentSelection: (document: Document) => void;
  toggleGroupSelection: (
    documents: Document[],
    allFilesNotProcessed: boolean,
    caseData?: Folder
  ) => void;
  toggleAllDocuments: (
    allDocuments: Document[],
    allFilesNotProcessed: boolean,
    caseData?: Folder
  ) => void;
  clearSelection: () => void;
  isDocumentSelected: (documentId: string) => boolean;
  isGroupFullySelected: (
    groupDocuments: Document[],
    allFilesNotProcessed: boolean,
    caseData?: Folder
  ) => boolean;
  isGroupPartiallySelected: (
    groupDocuments: Document[],
    allFilesNotProcessed: boolean,
    caseData?: Folder
  ) => boolean;
  getSelectedCount: () => number;
  isSelectionMode: () => boolean;
}

export const useDocumentSelectionStore = create<DocumentSelectionState>(
  (set, get) => ({
    selectedDocuments: [],

    toggleDocumentSelection: (document: Document) => {
      set((state) => {
        if (state.selectedDocuments.some((doc) => doc.id === document.id)) {
          return {
            selectedDocuments: state.selectedDocuments.filter(
              (doc) => doc.id !== document.id
            ),
          };
        } else {
          return {
            selectedDocuments: [
              ...state.selectedDocuments,
              toSelectedDocument(document),
            ],
          };
        }
      });
    },

    toggleGroupSelection: (
      documents: Document[],
      allFilesNotProcessed: boolean,
      caseData?: Folder
    ) => {
      const selectableDocuments = getSelectableDocuments(
        documents,
        allFilesNotProcessed,
        caseData
      );
      const selectableDocumentIds = selectableDocuments.map((doc) => doc.id);

      const allSelected =
        selectableDocuments.length > 0 &&
        selectableDocuments.every((doc) => get().isDocumentSelected(doc.id));

      if (allSelected) {
        set((state) => ({
          selectedDocuments: state.selectedDocuments.filter(
            (doc) => !selectableDocumentIds.includes(doc.id)
          ),
        }));
      } else {
        set((state) => {
          const newSelection = [...state.selectedDocuments];
          selectableDocuments.forEach((document) => {
            if (!newSelection.some((doc) => doc.id === document.id)) {
              newSelection.push(toSelectedDocument(document));
            }
          });
          return { selectedDocuments: newSelection };
        });
      }
    },

    toggleAllDocuments: (
      allDocuments: Document[],
      allFilesNotProcessed: boolean,
      caseData?: Folder
    ) => {
      const selectableDocuments = getSelectableDocuments(
        allDocuments,
        allFilesNotProcessed,
        caseData
      );

      const allSelected =
        selectableDocuments.length > 0 &&
        selectableDocuments.every((doc) => get().isDocumentSelected(doc.id));

      if (allSelected) {
        set({ selectedDocuments: [] });
      } else {
        set({ selectedDocuments: selectableDocuments.map(toSelectedDocument) });
      }
    },

    clearSelection: () => {
      set({ selectedDocuments: [] });
    },

    isDocumentSelected: (documentId: string) => {
      return get().selectedDocuments.some((doc) => doc.id === documentId);
    },

    isGroupFullySelected: (
      groupDocuments: Document[],
      allFilesNotProcessed: boolean,
      caseData?: Folder
    ) => {
      const selectableDocuments = getSelectableDocuments(
        groupDocuments,
        allFilesNotProcessed,
        caseData
      );
      return (
        selectableDocuments.length > 0 &&
        selectableDocuments.every((doc) => get().isDocumentSelected(doc.id))
      );
    },

    isGroupPartiallySelected: (
      groupDocuments: Document[],
      allFilesNotProcessed: boolean,
      caseData?: Folder
    ) => {
      const selectableDocuments = getSelectableDocuments(
        groupDocuments,
        allFilesNotProcessed,
        caseData
      );
      const anySelected = selectableDocuments.some((doc) =>
        get().isDocumentSelected(doc.id)
      );
      const allSelected = get().isGroupFullySelected(
        groupDocuments,
        allFilesNotProcessed,
        caseData
      );
      return anySelected && !allSelected;
    },

    getSelectedCount: () => {
      return get().selectedDocuments.length;
    },

    isSelectionMode: () => {
      return get().selectedDocuments.length > 0;
    },
  })
);
